import { useEffect, useMemo, useState } from 'react';
import { logError } from 'utils/logger';
import { Divider, Stack } from '@mui/material';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import { ActionPanel } from './ActionPanel';
import { TiersBuilder } from './tiersBuilder';
import { CriteriaBuilder } from './criteriaBuilder';
import { useScorecardBuilder } from './useScorecardBuilder';
import { ScoreCriteriaEntity, ScoreTierEntity } from './types';
import { ScorecardBuilderFallback } from './ScorecardBuilderFallback';
import { collectCreateAPIPayloadFromScorecard, collectUpdateAPIPayloadFromScorecard, mapCustomCriteria } from './lib';

interface Props {
  accountId: string;
}

export const ScorecardBuilderContainer = ({ accountId }: Props) => {
  const [criteriaBuilderValue, setCriteriaBuilderValue] = useState<ScoreCriteriaEntity[]>([]);
  const [tiersBuilderValue, setTiersBuilderValue] = useState<ScoreTierEntity[]>([]);

  const {
    isLoading,
    financeProgramId,
    scorecardCriteria,
    handleCreateScorecard,
    handleUpdateScorecard,
    handleUpdateCriteriaStatus,
  } = useScorecardBuilder();

  const onSubmit = async () => {
    try {
      if (scorecardCriteria?.prescreenCriteriaId) {
        const apiPayload = collectUpdateAPIPayloadFromScorecard(
          accountId,
          scorecardCriteria?.prescreenCriteriaId,
          criteriaBuilderValue,
          tiersBuilderValue
        );

        await handleUpdateScorecard(apiPayload);
      } else {
        const apiPayload = collectCreateAPIPayloadFromScorecard(criteriaBuilderValue, tiersBuilderValue);

        await handleCreateScorecard(apiPayload);
      }
    } catch (error) {
      logError(error);
    }
  };

  const onActivateDeactivate = async () => {
    try {
      const active = !scorecardCriteria?.active;
      await handleUpdateCriteriaStatus({ financeProgramId, active });
    } catch (error) {
      logError(error);
    }
  };

  const {
    criteriaBuilderInitialData,
    criteriaBuilderInitialColumnTypes,
    criteriaBuilderCustomCriteria,
    scoreTiersInitialData,
  } = useMemo(() => {
    return {
      criteriaBuilderInitialData:
        scorecardCriteria?.jsonCriteria?.scoreCriteria?.map((sc: any) => ({
          criteria: sc.criteria,
          customCriteria: mapCustomCriteria(sc.customCriteria ?? {}, 'internalId'),
        })) ?? [],
      criteriaBuilderInitialColumnTypes: [
        ...(scorecardCriteria?.jsonCriteria?.scoreCriteria?.[0]?.formRules ?? []).map((fr: any) => fr.fact),
      ],
      criteriaBuilderCustomCriteria: scorecardCriteria?.jsonCriteria?.scoreCriteria?.[0]?.customCriteria ?? {},
      scoreTiersInitialData: scorecardCriteria?.jsonCriteria?.scoreTiers ?? [],
    };
  }, [scorecardCriteria]);

  useEffect(() => {
    setCriteriaBuilderValue(criteriaBuilderInitialData);
    setTiersBuilderValue(scoreTiersInitialData);
  }, [criteriaBuilderInitialData, scoreTiersInitialData]);

  return (
    <RenderWithFallback isLoading={isLoading} fallBack={<ScorecardBuilderFallback />}>
      <CardContainer
        title="Builder"
        actionBtn={
          <ActionPanel
            onSubmit={onSubmit}
            scorecardCriteria={scorecardCriteria}
            onActivateDeactivate={onActivateDeactivate}
          />
        }
      >
        <Stack gap="2rem" mt="1rem">
          <CriteriaBuilder
            initialData={criteriaBuilderInitialData}
            initialColumnTypes={criteriaBuilderInitialColumnTypes}
            scoreCustomCriteria={criteriaBuilderCustomCriteria}
            onListingChange={setCriteriaBuilderValue}
          />

          <Divider />

          <TiersBuilder initialData={scoreTiersInitialData} onListingChange={setTiersBuilderValue} />
        </Stack>
      </CardContainer>
    </RenderWithFallback>
  );
};
